/**
 * api.js — typed API client
 *
 * In development:  Vite proxies /api → http://localhost:8787
 * In production:   set VITE_API_URL to your worker's URL (no trailing slash)
 *
 * Auth: a JWT (obtained via OAuth, see auth.jsx) is stored in localStorage and
 * sent as `Authorization: Bearer <token>` on every request. A 401 clears the
 * token and bounces the user to /login.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
const TOKEN_KEY = 'corridors_token';

export const token = {
  get:   ()  => localStorage.getItem(TOKEN_KEY),
  set:   (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: ()  => localStorage.removeItem(TOKEN_KEY),
};

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const t = token.get();
  if (t) headers.Authorization = `Bearer ${t}`;

  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, opts);

  if (res.status === 401) {
    token.clear();
    if (!location.pathname.startsWith('/login')) location.href = '/login';
    throw Object.assign(new Error('Session expired'), { status: 401 });
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data.error ?? `HTTP ${res.status}`), {
      status: res.status,
      code:   data.code,
    });
  }
  return data;
}

const get   = (path)        => request('GET',    path);
const post  = (path, body)  => request('POST',   path, body);
const put   = (path, body)  => request('PUT',    path, body);
const del   = (path)        => request('DELETE', path);
const patch = (path, body)  => request('PATCH',  path, body);

export const api = {
  // Current user + plan
  me: () => get('/api/me'),

  auth: {
    // Full URL the browser navigates to, to start OAuth.
    loginUrl: (provider) => `${BASE}/api/auth/${provider}`,
  },

  billing: {
    checkout: () => post('/api/billing/checkout'),
    portal:   () => post('/api/billing/portal'),
  },

  workflows: {
    list:   ()             => get('/api/workflows'),
    get:    (id)           => get(`/api/workflows/${id}`),
    create: (body)         => post('/api/workflows', body),
    update: (id, body)     => put(`/api/workflows/${id}`, body),
    delete: (id)           => del(`/api/workflows/${id}`),
    toggle: (id)           => patch(`/api/workflows/${id}/toggle`),
    trigger:(id, payload)  => post(`/api/workflows/${id}/trigger`, payload ?? {}),
  },

  runs: {
    list:   (workflowId, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`/api/workflows/${workflowId}/runs${qs ? '?' + qs : ''}`);
    },
    get:    (runId)   => get(`/api/runs/${runId}`),
    logs:   (runId)   => get(`/api/runs/${runId}/logs`),
  },
};

// Webhook URL helper (public endpoint — no /api prefix)
export function webhookUrl(workflowId) {
  const base = import.meta.env.VITE_API_URL ?? window.location.origin;
  return `${base}/webhook/${workflowId}`;
}
